/**
 * activationAdapter — les charges de « Activer la suite » → formes `UI*`.
 *
 * Deux surfaces, une seule chaîne de dérivation :
 * - l'ARRIVÉE du lien signé (`ActivationLandingScreen.js`) : six états, un produit, une carte
 *   d'offre ou un avis ;
 * - le COMPOSEUR (`ActivationComposerScreen.js`) : huit sujets de choix et le récapitulatif
 *   tarifaire, recalculés à chaque geste — prix affiché = prix facturé.
 *
 * API-aware : il connaît `upcoming_activation`, `planned`, `pricing_table_v2`, `sessions_per_week`.
 * Le design system n'en voit rien — tout sort déjà formaté.
 *
 * Symboles legacy portés, avec leur fichier :
 * - `landingState` / `canActivateSuite` — `common/components/family/activation/landingState.js`
 * - les cartes `ActivationSuiteOfferCard` / `PlannedActivationCard` —
 *   `common/components/family/activation/ActivationCards.js`
 * - les dérivations d'affichage — `common/utils/activationSuiteDisplay.js`, recopiées dans
 *   `adapters/parent/shared/activationDisplay.ts`
 * - la grille tarifaire `getOfferPricing` / `getOfferPrice` — `new-subscription/v2/newSubscriptionUtils.js`
 * - la répartition par défaut `generateDefaultSubjectHoursAllocation` —
 *   `common/hooks/subscription/useNewSubscriptionStepper.js:552-564`
 *
 * ⚠️ Les écrans legacy de l'activation sont retirés ; les symboles que d'autres parcours lisent
 * encore (dérivations d'affichage, grille tarifaire, répartition par défaut) restent une seconde
 * copie : une évolution d'une règle commune se reporte ici.
 */
import moment from "moment"

import { studentClasses } from "../../../../../data/constants/formConstants"
import { toUISubject, type ApiSchoolSubject } from "../shared/schoolSubjectAdapter"
import {
    ACTIVATION_FLEXIBILITY_POINTS,
    activationClassTitle,
    canonicalSlotsToGridSlots,
    canonicalSlotsToSelectableGridSlots,
    formatActivationDate,
    formatActivationPrice,
    formatSlotsLabel,
    getActivationHeadline,
    getActivationPriceLine,
    getDiscountUrgency,
    getLoyaltyDiscount,
    getProductEndLine,
    getRepriseHero,
    getResumeDateOptions,
    gridDisplayOffsetHours,
    isProductCoverageOver,
    partialMonthHours,
    type ResumeDateOption,
} from "../shared/activationDisplay"
import { getSummerConfigSafe } from "../Subscription/summerGates"
import {
    type UIActivationAllocationRow,
    type UIActivationChoice,
    type UIActivationLandingState,
    type UIActivationNotice,
    type UIActivationOutcome,
    type UIActivationPriceSummary,
    type UIActivationProduct,
    type UISubject,
    type UISubscriptionLine,
    type UISubscriptionNextStep,
} from "../../../ui/types"

// ── Formes de charge, toutes optionnelles (règle des adaptateurs) ─────────────────────────────

/** `upcoming_activation.planned` — `SubscriptionDueDateEvaluator:1094-1152`. */
export type ApiPlannedActivation = {
    milestone_id?: string | number | null
    effective_at?: string | null
    subject_ids?: (string | number)[] | null
    hours_allocation?: { subject_id?: string | number; hours?: number }[] | null
    resume_slots?: string[] | null
    offer_type?: string | null
    offer_label?: string | null
    hours_per_week?: number | null
    hours_per_month?: number | null
    with_commitment?: boolean | null
    commitment_running?: boolean | null
    monthly_price?: number | null
    first_month_price?: number | null
    loyalty_discount_total?: number | null
    loyalty_discount_installments?: boolean | null
    source?: string | null
    /** Droit parental d'ANNULER — jamais le droit de modifier. */
    editable?: boolean | null
    /** Droit de MODIFIER l'offre, quelle que soit la source du jalon. */
    modifiable?: boolean | null
    pause_chained?: boolean | null
    is_past_due?: boolean | null
}

/** `upcoming_activation.recommended_offer` — `SubscriptionDueDateEvaluator:936-950`. */
export type ApiRecommendedOffer = {
    offer_type?: string | null
    offer_label?: string | null
    hours_per_week?: number | null
    hours_per_month?: number | null
    with_commitment?: boolean | null
    commitment_running?: boolean | null
    monthly_price?: number | null
    first_month_price?: number | null
    loyalty_eligible?: boolean | null
    loyalty_discount_total?: number | null
    loyalty_discount_installments?: boolean | null
    resume_slots?: string[] | null
    subject_ids?: (string | number)[] | null
    hours_allocation?: Record<string, number> | { subject_id?: string | number; hours?: number }[] | null
}

/** `upcoming_activation` — `SubscriptionDueDateEvaluator:321-342` (et `:1257-1281` pour l'enveloppe vide). */
export type ApiUpcomingActivation = {
    type?: string | null
    /** Stage tenu pendant l'année : la suite reprend le lundi qui le suit, sans « rentrée ». */
    held_in_year?: boolean | null
    eligible?: boolean | null
    reason?: string | null
    due_date?: string | null
    resume_date?: string | null
    discount_expires_at?: string | null
    discount_window_open?: boolean | null
    loyalty_discount_amount?: number | null
    extendable_to?: string | null
    recommended_offer?: ApiRecommendedOffer | null
    planned?: ApiPlannedActivation | null
    /** Dates de l'ÉQUIPE, servies sous impersonation seulement (`:215`). */
    admin_resume_dates?: string[] | null
    /**
     * Après un stage de l'année, la date de reprise est LIBRE : la première qu'on peut choisir
     * (J+2, la règle des 48 h). Null → le choix est la liste fermée de l'été.
     */
    earliest_resume_date?: string | null
}

/** Une case de `Subscription::$pricesAmountsV2[offre][heures_par_mois]`. */
export type ApiOfferPrice = { base?: number | null; commitment?: number | null }
export type ApiPricingTableV2 = Record<string, Record<string, ApiOfferPrice> | undefined>

/** `summer_config` — la charge du lien n'en porte que deux clés. */
export type ApiActivationSummerConfig = {
    current_year?: number | null
    window_end?: string | null
    resume_date?: string | null
    activation_resume_dates?: string[] | null
}

/** La charge du lien signé — `App\Services\FSM\Subscription\ActivationLandingPayload`. */
export type ApiActivationLinkContext = {
    product_label?: string | null
    student?: { id?: string | number; firstname?: string | null; class?: string | null } | null
    upcoming_activation?: ApiUpcomingActivation | null
    self_serve_reprise?: { eligible?: boolean | null; season?: string | null } | null
    pricing_table_v2?: ApiPricingTableV2 | null
    self_serve_no_commitment_hours?: Record<string, number> | null
    sessions_per_week?: Record<string, number> | null
    school_subjects?: ApiSchoolSubject[] | null
    summer_config?: ApiActivationSummerConfig | null
}

// ── L'arrivée ────────────────────────────────────────────────────────────────────────────────

/** Les deux façons dont la page n'a rien à montrer (`ActivationLandingScreen.js:68-76`). */
export const ACTIVATION_EXPIRED_LINK_OUTCOME: UIActivationOutcome = {
    title: "Ce lien n'est plus valide",
    body: "Retrouvez l'abonnement de votre enfant dans votre espace.",
}

export const ACTIVATION_UNREADABLE_OFFER_OUTCOME: UIActivationOutcome = {
    title: "Impossible d'afficher votre offre",
    body: "Réessayez dans un instant.",
}

/** Table de routage `reason` → état de page (`landingState.js:35-38`). */
const PAGE_STATE_BY_REASON: Record<string, UIActivationLandingState> = {
    error: "unavailable",
    paused: "paused",
}

/**
 * Y a-t-il une suite à ACTIVER aujourd'hui ? Port littéral de `canActivateSuite`
 * (`landingState.js:63-67`) — `recommended_offer` fait partie de la condition : une surface qui
 * promet une décision doit mener à l'écran qui la porte.
 */
export function canActivateSuite(activation: ApiUpcomingActivation | null | undefined): boolean {
    return !!activation && activation.type !== "none" && !!activation.eligible && !!activation.recommended_offer
}

/** Port littéral de `landingState` (`landingState.js:69-93`) — l'ordre des six tranches compte. */
export function activationLandingState(context: ApiActivationLinkContext | null | undefined): UIActivationLandingState {
    const activation = context?.upcoming_activation

    // Un jalon déjà posé l'emporte sur tout, remise expirée comprise.
    if (activation?.planned) return "already_planned"
    if (canActivateSuite(activation)) return "activable"

    const explicitState = activation?.reason ? PAGE_STATE_BY_REASON[activation.reason] : undefined
    if (explicitState) return explicitState

    if (context?.self_serve_reprise?.eligible) return "reprise"

    return "nothing_to_activate"
}

function offerLineOf(label: string | null | undefined, hoursPerWeek: number | null | undefined): string | null {
    const line = [label, hoursPerWeek ? `${hoursPerWeek}h/sem` : null].filter(Boolean).join(" · ")

    return line === "" ? null : line
}

/**
 * La carte « Et ensuite ? » de l'état `activable` — `ActivationSuiteOfferCard`
 * (`ActivationCards.js:213-355`), sans son slot `extra` : le trou de couverture appartient au
 * portail.
 */
function toActivateNextStep(
    activation: ApiUpcomingActivation,
    firstName: string,
    now?: string | Date
): UISubscriptionNextStep {
    const offer = activation.recommended_offer ?? {}
    const { deadlineLabel } = getDiscountUrgency(activation.discount_expires_at, now)
    const resumeLabel = formatActivationDate(activation.resume_date)
    const detailLines = [
        offer.loyalty_eligible && deadlineLabel ? `Tarif valable jusqu'au ${deadlineLabel}` : null,
        resumeLabel ? `Reprise le ${resumeLabel}` : null,
    ].filter((line): line is string => line != null)

    return {
        variant: "activate",
        pillLabel: "Et ensuite ?",
        pillIcon: "bolt",
        headline: getActivationHeadline(activation.type, firstName, !!activation.held_in_year),
        offerLine: offerLineOf(offer.offer_label, offer.hours_per_week),
        priceLine: getActivationPriceLine(offer.monthly_price, offer.first_month_price),
        priceDiscounted:
            offer.first_month_price != null &&
            offer.monthly_price != null &&
            offer.first_month_price < offer.monthly_price,
        detailLines,
        guarantees: ACTIVATION_FLEXIBILITY_POINTS,
    }
}

/**
 * La carte « Reprise planifiée » — `PlannedActivationCard` (`ActivationCards.js:370-504`).
 *
 * Ses créneaux se lisent à l'heure de la famille (`displayOffsetHours`, mesuré à la date de la
 * reprise), comme la grille du composeur qu'ouvre son bouton : sans le même écart, la carte
 * disait « Mardi 19h-20h » et la grille « 18h-19h ». Ils restent TOUS lus, hors grille compris :
 * un créneau posé se dit même quand on ne le vend plus.
 *
 * ⚠️ Le geste de modification est gardé par `modifiable`, PAS par `editable` : ce dernier est le
 * droit parental d'ANNULER, et le lire ici privait de tout geste les familles dont la reprise
 * vient de l'équipe, du programme d'été ou d'une migration. Aucune annulation sur l'arrivée,
 * quelle que soit la source : elle appartient au portail.
 */
function toPlannedNextStep(
    planned: ApiPlannedActivation,
    subjects: ApiSchoolSubject[],
    displayOffsetHours: number
): UISubscriptionNextStep {
    const resumeLabel = formatActivationDate(planned.effective_at)
    const inProgress = !!planned.is_past_due
    const slotsLine = formatSlotsLabel(canonicalSlotsToGridSlots(planned.resume_slots ?? []), displayOffsetHours)
    const installments = !!planned.loyalty_discount_installments && (planned.loyalty_discount_total ?? 0) > 0
    const detailLines = [
        slotsLine ? `Dispo : ${slotsLine}` : null,
        installments
            ? `Remise : −50 € sur chacun des ${Math.round((planned.loyalty_discount_total ?? 0) / 50)} premiers mois`
            : null,
    ].filter((line): line is string => line != null)

    return {
        variant: "planned",
        pillLabel: inProgress ? "Reprise en cours d'activation" : "Reprise planifiée",
        pillIcon: inProgress ? "hourglass-half" : "check-circle",
        headline: resumeLabel ? `Reprise le ${resumeLabel}` : "Reprise planifiée",
        offerLine: offerLineOf(planned.offer_label, planned.hours_per_week),
        priceLine: getActivationPriceLine(planned.monthly_price, planned.first_month_price),
        priceDiscounted:
            planned.first_month_price != null &&
            planned.monthly_price != null &&
            planned.first_month_price < planned.monthly_price,
        detailLines,
        // Une chip par MATIÈRE et non par objectif : la suite n'a pas commencé, il n'y a pas
        // d'objectif pédagogique à grouper (`ActivationCards.js:430-457`).
        objectives: subjects.map((subject) => ({
            id: subject.id ?? 0,
            subjects: [toUISubject(subject)],
        })),
        editable: planned.modifiable !== false,
    }
}

export type UIActivationLanding = {
    state: UIActivationLandingState
    studentFirstName: string
    product: UIActivationProduct | null
    nextStep: UISubscriptionNextStep | null
    notice: UIActivationNotice | null
    outcome: UIActivationOutcome | null
    /** La saison du funnel de reprise — l'événement Amplitude du geste la porte. */
    repriseSeason: string | null
}

/**
 * Les deux horloges d'un écran de créneaux : celle de la famille, celle du serveur. Vides pour un
 * visiteur anonyme du lien signé (la charge du lien ne porte pas de fuseau) — il lit alors les
 * heures de Paris, sur la carte comme dans la grille.
 */
export type ActivationTimezones = { userTimezone?: string | null; serverTimezone?: string | null }

/**
 * La charge du lien → ce que `<ActivationLandingPage>` rend.
 *
 * Elle raconte la situation à l'INSTANT DU CLIC : le lien n'expire pas, et une remise expirée est
 * simplement absente de l'offre, jamais affichée comme si elle courait encore.
 *
 * `timezones` : les mêmes sources que le composeur qu'ouvre le bouton de la carte
 * (`ActivationLandingScreen.js`, `mapStateToProps`), pour que la carte et la grille disent la même
 * heure.
 */
export function toUIActivationLanding(
    context: ApiActivationLinkContext | null | undefined,
    now?: string | Date,
    timezones: ActivationTimezones = {}
): UIActivationLanding {
    const state = activationLandingState(context)
    const activation = context?.upcoming_activation ?? null
    const planned = activation?.planned ?? null
    const firstName = context?.student?.firstname || "votre enfant"
    const repriseSeason = context?.self_serve_reprise?.season ?? null

    // L'évaluateur a levé et dégradé : le raconter comme « il n'y a pas de suite » serait affirmer
    // une situation qu'on n'a justement pas su lire.
    if (state === "unavailable") {
        return {
            state,
            studentFirstName: firstName,
            product: null,
            nextStep: null,
            notice: null,
            outcome: ACTIVATION_UNREADABLE_OFFER_OUTCOME,
            repriseSeason,
        }
    }

    const productLabel = context?.product_label
    const product: UIActivationProduct | null = productLabel
        ? {
              label: productLabel,
              endLine: getProductEndLine(activation?.type, activation?.due_date, now),
              // La flamme suit la COUVERTURE, pas la carte.
              running: !isProductCoverageOver(activation?.due_date, now),
          }
        : null

    let nextStep: UISubscriptionNextStep | null = null
    let notice: UIActivationNotice | null = null

    if (state === "already_planned" && planned) {
        const plannedSubjectIds = (planned.subject_ids ?? []).map(String)
        nextStep = toPlannedNextStep(
            planned,
            (context?.school_subjects ?? []).filter((subject) => plannedSubjectIds.includes(String(subject?.id))),
            // Mesuré à la DATE DE LA REPRISE : Paris change d'heure, Alger non.
            gridDisplayOffsetHours(timezones.userTimezone, timezones.serverTimezone, planned.effective_at)
        )
    } else if (state === "activable" && activation) {
        nextStep = toActivateNextStep(activation, firstName, now)
    } else if (state === "reprise") {
        // Même proposition, même formulation et même rang de priorité que la bannière de l'accueil.
        const hero = getRepriseHero(repriseSeason, firstName)
        notice = { title: hero.headline, body: hero.subline, actionLabel: "Reprendre l'accompagnement" }
    } else if (state === "paused") {
        // Une pause EST une trajectoire : c'est la seule chose que le parent doit lire.
        notice = { title: "Une pause est en place", body: "Il n'y a rien à activer tant qu'elle court." }
    } else {
        notice = { title: "Rien à activer pour le moment", body: "Tout reste accessible depuis votre espace." }
    }

    return { state, studentFirstName: firstName, product, nextStep, notice, outcome: null, repriseSeason }
}

// ── Le composeur ─────────────────────────────────────────────────────────────────────────────

/** Les deux formules vendues en self-service — le Mode Autonome reste posable par l'équipe seule. */
export const ACTIVATION_OFFER_OPTIONS: UIActivationChoice[] = [
    { value: "essential", label: "Cours partagés", description: "avec un autre élève", badge: "Le plus choisi" },
    { value: "integral", label: "Cours particuliers", description: "seul avec son tuteur" },
]

const OFFER_TYPE_VALUES = ACTIVATION_OFFER_OPTIONS.map((option) => String(option.value))

/** Un seul cran : douze mois. La sortie est l'interrupteur, pas une deuxième carte. */
export const ACTIVATION_COMMITMENT_OPTIONS: UIActivationChoice<number>[] = [
    { value: 12, label: "12", description: "mois" },
]

/** L'échelle des classes du back (`User::$studentClasses`), « Autre » compris. */
export const ACTIVATION_CLASS_OPTIONS: UIActivationChoice[] = studentClasses.map((value: string) => ({
    value,
    label: value,
}))

/** Classes autorisées à choisir plus de matières que d'heures (3ème et avant). */
const CLASSES_ALLOW_MORE_SUBJECTS: string[] = studentClasses.slice(0, studentClasses.indexOf("3ème") + 1)

/**
 * Jusqu'à la 3ème, on peut choisir plus de matières que d'heures — même règle que le funnel
 * `ChangePlanBottomModal`. La classe qui fait foi est celle CHOISIE dans le formulaire.
 */
export function allowsMoreSubjectsThanHours(className: string | null | undefined): boolean {
    return className != null && CLASSES_ALLOW_MORE_SUBJECTS.includes(className)
}

/**
 * Formule de départ ramenée à une formule PROPOSABLE : le back connaît des formules que ce
 * parcours ne vend pas, et une valeur hors picker laisserait l'écran sans prix.
 */
export function resolveActivationOfferType(rawOfferType: string | null | undefined): string {
    return rawOfferType != null && OFFER_TYPE_VALUES.includes(rawOfferType) ? rawOfferType : "essential"
}

/** La projection back arrive en LISTE d'objets ; le formulaire édite une map `{id: heures}`. */
export function allocationBySubject(
    raw: ApiRecommendedOffer["hours_allocation"] | null | undefined
): Record<string, number> {
    if (Array.isArray(raw)) {
        return raw.reduce<Record<string, number>>((acc, entry) => {
            if (entry?.subject_id != null && entry.hours != null) acc[String(entry.subject_id)] = entry.hours
            return acc
        }, {})
    }

    return { ...((raw as Record<string, number> | null | undefined) ?? {}) }
}

/**
 * Répartition égale par défaut — port littéral de `generateDefaultSubjectHoursAllocation`
 * (`useNewSubscriptionStepper.js:552-564`). Sans amorce, les +/- partent de `undefined` et
 * produisent des NaN.
 */
export function defaultSubjectHoursAllocation(
    subjectIds: (string | number)[],
    hoursPerWeek: number
): Record<string, number> {
    const baseHours = Math.floor(hoursPerWeek / subjectIds.length)
    const extraHours = hoursPerWeek % subjectIds.length

    const allocation: Record<string, number> = {}
    subjectIds.forEach((subjectId, index) => {
        allocation[String(subjectId)] = baseHours + (index < extraHours ? 1 : 0)
    })

    return allocation
}

/** Ce que le parent est en train de composer — et, tel quel, ce que le POST portera. */
export type ActivationDraft = {
    offerType: string
    hoursPerWeek: number
    withCommitment: boolean
    /**
     * Forme GRILLE (« Monday 17h-18h »), en heures de PARIS — c'est l'offre, ce qui part et ce que
     * le serveur valide. Seules les cases que la grille affiche : un créneau hors grille n'a pas
     * de case pour être décoché, et le serveur reporte lui-même ceux qu'une offre vend.
     */
    slots: string[]
    pickedClass: string | null
    subjectIds: (string | number)[]
    hoursAllocation: Record<string, number>
    pickedResumeDate: string | null
}

/** Le contexte du composeur, résolu depuis le lien OU depuis Redux — voir le view-model. */
export type ActivationComposerSources = {
    activation: ApiUpcomingActivation | null
    studentClass: string | null
    schoolSubjects: ApiSchoolSubject[]
    pricingTableV2: ApiPricingTableV2 | null
    selfServeNoCommitmentHours: Record<string, number> | null
    sessionsPerWeek: Record<string, number> | null
    summerConfig: ApiActivationSummerConfig | null
    /** Entrée par lien signé : la réponse du lien fait foi, et le POST change de porte. */
    fromLink: boolean
} & ActivationTimezones

/**
 * L'offre de départ : celle du JALON en modification, sinon la recommandation du back
 * (`ActivationComposerScreen.js:211-224`).
 */
function baselineOffer(activation: ApiUpcomingActivation | null): ApiRecommendedOffer | null {
    const planned = activation?.planned
    if (!planned) return activation?.recommended_offer ?? null

    return {
        offer_type: planned.offer_type,
        hours_per_week: planned.hours_per_week,
        with_commitment: planned.with_commitment,
        commitment_running: planned.commitment_running,
        resume_slots: planned.resume_slots,
        subject_ids: planned.subject_ids,
        hours_allocation: allocationBySubject(planned.hours_allocation ?? {}),
        loyalty_discount_installments: planned.loyalty_discount_installments,
    }
}

/**
 * Le palier UNIQUE vendu sans engagement, par formule, en heures par SEMAINE.
 *
 * La règle vient du back (`Subscription::SELF_SERVE_NO_COMMITMENT_HOURS`), exprimée en heures par
 * mois : la table de cadence la convertit. Absente, on ne restreint rien — la garde serveur reste
 * le filet.
 */
export function soldNoCommitmentHoursByOffer(sources: ActivationComposerSources): Record<string, number | null> {
    const out: Record<string, number | null> = {}
    OFFER_TYPE_VALUES.forEach((offer) => {
        const hoursPerMonth = sources.selfServeNoCommitmentHours?.[offer]
        out[offer] = hoursPerMonth ? (sources.sessionsPerWeek?.[hoursPerMonth] ?? null) : null
    })

    return out
}

/** Les dates proposées : celles du back, ou son repli de fin de fenêtre. */
function proposedResumeDates(sources: ActivationComposerSources): string[] {
    const proposed = sources.summerConfig?.activation_resume_dates

    return proposed && proposed.length > 0 ? proposed : [getSummerConfigSafe(sources.summerConfig).resumeDate]
}

export function activationResumeDateOptions(
    sources: ActivationComposerSources,
    now?: string | Date | null
): { options: ResumeDateOption[]; selected: string | null } {
    const activation = sources.activation
    const currentDate = activation?.planned?.effective_at ?? activation?.resume_date ?? null

    return getResumeDateOptions({
        proposedDates: proposedResumeDates(sources),
        adminDates: activation?.admin_resume_dates ?? null,
        currentDate,
        now,
    })
}

/**
 * L'amorce du brouillon : l'offre de départ, la classe ACTUELLE du profil (jamais incrémentée —
 * des familles l'ont déjà mise à jour elles-mêmes), et la date présélectionnée.
 */
export function toActivationDraftSeed(sources: ActivationComposerSources, now?: string | Date | null): ActivationDraft {
    const offer = baselineOffer(sources.activation)
    const hoursPerWeek = offer?.hours_per_week ?? 2
    const subjectIds = offer?.subject_ids ?? []
    const commitmentLocked = offer?.commitment_running === true
    const seededAllocation = allocationBySubject(offer?.hours_allocation)

    return {
        offerType: resolveActivationOfferType(offer?.offer_type),
        hoursPerWeek,
        withCommitment: commitmentLocked ? true : (offer?.with_commitment ?? true),
        // Sans les cases hors grille : la modale n'a pas de quoi les décocher, et le submit
        // poste la sélection ENTIÈRE. Le dimanche suit la formule de départ.
        slots: canonicalSlotsToSelectableGridSlots(offer?.resume_slots ?? [], {
            includeSunday: resolveActivationOfferType(offer?.offer_type) === "integral",
        }),
        pickedClass: sources.studentClass ?? null,
        subjectIds,
        hoursAllocation:
            Object.keys(seededAllocation).length > 0
                ? seededAllocation
                : subjectIds.length > 0 && subjectIds.length <= hoursPerWeek
                  ? defaultSubjectHoursAllocation(subjectIds, hoursPerWeek)
                  : {},
        pickedResumeDate: activationResumeDateOptions(sources, now).selected,
    }
}

type OfferPricingRow = { weeklyHours: number | undefined; hoursPerMonth: number; hourlyPrice: number | undefined }

/** Port littéral de `getOfferPricing` (`newSubscriptionUtils.js:3-22`). */
function offerPricing(sources: ActivationComposerSources, offerType: string, withCommitment: boolean) {
    const table = sources.pricingTableV2?.[offerType] ?? {}
    const pricingType = withCommitment ? "commitment" : "base"

    return Object.keys(table).reduce<Record<string, OfferPricingRow>>((acc, hoursPerMonth) => {
        const weeklyHours = sources.sessionsPerWeek?.[hoursPerMonth]
        acc[String(weeklyHours)] = {
            weeklyHours,
            hoursPerMonth: Number(hoursPerMonth),
            hourlyPrice: table[hoursPerMonth]?.[pricingType] ?? undefined,
        }
        return acc
    }, {})
}

/** La répartition a-t-elle un sens ? Plusieurs matières, et strictement moins que d'heures. */
export function canAllocateHours(draft: ActivationDraft): boolean {
    return draft.subjectIds.length > 1 && draft.subjectIds.length < draft.hoursPerWeek
}

/** Le corps du POST d'activation — `ActivationComposerScreen.js:637-699`. */
export type ActivationSubmission = {
    body: Record<string, unknown>
    /** Les propriétés de l'événement `activation_confirmed`. */
    tracking: { offer_type: string; hours_per_week: number; with_commitment: boolean; subjects_count: number }
}

export type ActivationComposerModel = {
    editing: boolean
    /** « Classe à la rentrée », ou « Classe » après un stage de l'année. */
    classTitle: string
    classOptions: UIActivationChoice[]
    selectedClass: string | null
    hoursOptions: UIActivationChoice<number>[]
    selectedHours: number
    hoursNote: string | null
    selectedSubjects: UISubject[]
    allocationRows: UIActivationAllocationRow[]
    slotsCount: number
    slotsSummary: string | null
    resumeDateOptions: UIActivationChoice[]
    /** Date libre (après un stage de l'année) : la première choisissable. Null → la liste fermée. */
    resumeDateMin: Date | null
    selectedResumeDate: string | null
    lockedResumeDateLine: string | null
    lockedResumeDateNote: string | null
    offerOptions: UIActivationChoice[]
    selectedOffer: string
    commitmentOptions: UIActivationChoice<number>[]
    withCommitment: boolean
    guarantees: UISubscriptionLine[]
    summary: UIActivationPriceSummary | null
    /**
     * Ce que rien ne rend : de quoi poster, et de quoi configurer les deux modales restées
     * legacy. Le catalogue de matières voyage brut parce que c'est la modale legacy qui le rend
     * (icônes comprises) — le pont le reçoit en prop et ne l'importe jamais depuis les ports.
     */
    submission: ActivationSubmission | null
    subjectsPicker: {
        subjects: ApiSchoolSubject[]
        selectedIds: (string | number)[]
        maxSubjects: number | null
        homeworkSupportFrom: number | null
    }
    /**
     * `userTimezone` / `serverTimezone` / `at` : la modale AFFICHE ses cases à l'heure de la
     * famille, mesurée à la date de reprise retenue, et le dit ; les cases cochées restent en
     * heures de Paris.
     */
    slotsPicker: {
        slots: string[]
        includeSunday: boolean
        weeklyHours: number
        userTimezone: string | null
        serverTimezone: string | null
        at: string | null
    }
}

/**
 * Le composeur entier, recalculé à chaque geste : les huit sujets de choix, le récapitulatif
 * tarifaire, et le corps du POST.
 *
 * Aucune règle ne reste au design system : « la répartition a-t-elle un sens ? », « ce palier
 * est-il vendu sans engagement ? », « reste-t-il une date à proposer ? » se répondent ici et
 * descendent en listes vides ou en rappels absents.
 */
export function toActivationComposerModel(
    sources: ActivationComposerSources,
    draft: ActivationDraft,
    now?: string | Date | null
): ActivationComposerModel {
    const activation = sources.activation
    const planned = activation?.planned ?? null
    const isModification = !!planned
    const offer = baselineOffer(activation)
    const commitmentLocked = offer?.commitment_running === true

    const noCommitmentHoursPerWeek = soldNoCommitmentHoursByOffer(sources)[draft.offerType] ?? null
    const hoursLockedByNoCommitment = !draft.withCommitment && noCommitmentHoursPerWeek !== null

    const pricing = offerPricing(sources, draft.offerType, draft.withCommitment)
    const hoursOptions = Object.values(pricing)
        .filter((row) => row.weeklyHours != null)
        .filter((row) => !hoursLockedByNoCommitment || row.weeklyHours === noCommitmentHoursPerWeek)
        .map((row) => ({
            value: row.weeklyHours as number,
            label: `${row.weeklyHours}h`,
            // « Le plus choisi » sur 3 h : consigne owner — le funnel le tire de la reco serveur,
            // absente ici.
            badge: row.weeklyHours === 3 ? "Le plus choisi" : null,
        }))

    const selectedRow = pricing[String(draft.hoursPerWeek)]
    const hourlyPrice = selectedRow?.hourlyPrice ?? null
    const hoursPerMonth = selectedRow?.hoursPerMonth ?? null
    // `getOfferPrice` lève sur un miss de grille : le garde-fou reste ici, comme dans le legacy.
    const monthlyPrice =
        hourlyPrice != null && hoursPerMonth != null
            ? Math.round(Math.round(hourlyPrice * hoursPerMonth * 10) / 10)
            : null
    // Offre non calculable (miss de grille) → jamais 0 €, et la confirmation est bloquée.
    const isComputable = monthlyPrice != null && monthlyPrice > 0

    // Après un stage de l'année, la date se choisit au calendrier, à partir de J+2 : la liste
    // fermée de l'été ne s'applique pas, et la date ne se verrouille que si une pause la porte.
    const freeResumeFrom = activation?.earliest_resume_date ?? null
    const resumeOptions = freeResumeFrom ? [] : activationResumeDateOptions(sources, now).options
    const resumeDateLocked =
        isModification &&
        (freeResumeFrom ? planned?.pause_chained === true : resumeOptions.every((option) => option.adHoc))
    const currentResumeDate = planned?.effective_at ?? activation?.resume_date ?? null
    const effectiveResumeDate = draft.pickedResumeDate ?? currentResumeDate
    // Les créneaux NOMMENT des heures de Paris ; ils s'affichent à l'heure de la famille, ici comme
    // dans la modale. Mesuré À LA DATE DE LA REPRISE : une grille de septembre lue en janvier se
    // décalerait d'une heure de travers (Alger ne change pas d'heure, Paris si).
    const displayOffsetHours = gridDisplayOffsetHours(sources.userTimezone, sources.serverTimezone, effectiveResumeDate)

    // Remise fidélité : le barème et la fenêtre viennent du serveur ; seules les conditions que le
    // parent peut encore changer (engagement, volume) sont évaluées sur l'offre RETENUE.
    const inPlaceMilestoneId =
        planned && planned.editable === false && planned.modifiable !== false ? (planned.milestone_id ?? null) : null
    const { eligible: loyaltyEligible, amount: loyaltyAmount } = getLoyaltyDiscount({
        grantedAmount: planned?.loyalty_discount_total ?? 0,
        catalogAmount: activation?.loyalty_discount_amount ?? 0,
        windowOpen: !!activation?.discount_window_open,
        inPlaceModification: inPlaceMilestoneId != null,
        withCommitment: draft.withCommitment,
        hoursPerWeek: draft.hoursPerWeek,
    })

    const summary = isComputable
        ? buildPriceSummary({
              monthlyPrice: monthlyPrice as number,
              hourlyPrice,
              hoursPerMonth,
              effectiveResumeDate,
              loyaltyEligible,
              loyaltyAmount,
              installments: !!offer?.loyalty_discount_installments,
          })
        : null

    const selectedApiSubjects = sources.schoolSubjects.filter((subject) =>
        draft.subjectIds.map(String).includes(String(subject?.id))
    )
    const allocatable = canAllocateHours(draft)
    const allowMoreSubjects = allowsMoreSubjectsThanHours(draft.pickedClass ?? sources.studentClass)

    return {
        editing: isModification,
        classTitle: activationClassTitle(!!sources.activation?.held_in_year),
        classOptions: ACTIVATION_CLASS_OPTIONS,
        selectedClass: draft.pickedClass,
        hoursOptions,
        selectedHours: draft.hoursPerWeek,
        hoursNote: hoursLockedByNoCommitment
            ? `L'offre sans engagement est disponible en ${noCommitmentHoursPerWeek}h par semaine. Choisissez l'engagement 12 mois pour un autre volume.`
            : null,
        selectedSubjects: selectedApiSubjects.map(toUISubject),
        allocationRows: allocatable
            ? selectedApiSubjects.map((subject) => ({
                  subject: toUISubject(subject),
                  hours: draft.hoursAllocation[String(subject?.id)] ?? 0,
                  // Le plafond d'UNE ligne : les heures restantes une fois une heure réservée à
                  // chacune des autres matières (`HoursAllocationSection.js:30`).
                  maxHours: draft.hoursPerWeek - (selectedApiSubjects.length - 1),
              }))
            : [],
        slotsCount: draft.slots.length,
        slotsSummary: draft.slots.length > 0 ? formatSlotsLabel(draft.slots, displayOffsetHours) : null,
        resumeDateOptions: resumeOptions.map((option) => ({
            value: option.value,
            label: formatActivationDate(option.value) ?? option.value,
        })),
        resumeDateMin: freeResumeFrom && !resumeDateLocked ? moment(freeResumeFrom).toDate() : null,
        selectedResumeDate: draft.pickedResumeDate,
        lockedResumeDateLine:
            resumeDateLocked && currentResumeDate ? `Reprise le ${formatActivationDate(currentResumeDate)}` : null,
        lockedResumeDateNote:
            resumeDateLocked && currentResumeDate && planned?.pause_chained === true
                ? "Date portée par la pause — pour la changer, modifiez la pause."
                : null,
        offerOptions: ACTIVATION_OFFER_OPTIONS,
        selectedOffer: draft.offerType,
        // La section disparaît quand l'engagement court déjà : il n'y a rien à choisir.
        commitmentOptions: commitmentLocked ? [] : ACTIVATION_COMMITMENT_OPTIONS,
        withCommitment: draft.withCommitment,
        guarantees: ACTIVATION_FLEXIBILITY_POINTS,
        summary,
        submission: isComputable
            ? {
                  body: buildSubmissionBody({
                      draft,
                      allocatable,
                      resumeDateLocked,
                      resumeOptions,
                      freeResumeFrom,
                      inPlaceMilestoneId,
                      studentClass: sources.studentClass ?? null,
                  }),
                  tracking: {
                      offer_type: draft.offerType,
                      hours_per_week: draft.hoursPerWeek,
                      with_commitment: draft.withCommitment,
                      subjects_count: draft.subjectIds.length,
                  },
              }
            : null,
        subjectsPicker: {
            subjects: sources.schoolSubjects,
            selectedIds: draft.subjectIds,
            maxSubjects: allowMoreSubjects ? null : draft.hoursPerWeek,
            homeworkSupportFrom: allowMoreSubjects && draft.hoursPerWeek > 0 ? draft.hoursPerWeek : null,
        },
        slotsPicker: {
            slots: draft.slots,
            includeSunday: draft.offerType === "integral",
            weeklyHours: draft.hoursPerWeek,
            userTimezone: sources.userTimezone ?? null,
            serverTimezone: sources.serverTimezone ?? null,
            at: effectiveResumeDate,
        },
    }
}

/**
 * Le récapitulatif tarifaire — « prix affiché = prix facturé ».
 *
 * Le 1ᵉʳ mois RÉEL : une reprise en cours de mois est proratisée aux heures restantes
 * (`partialMonthHours`, miroir de `PartialMonthHoursDomain`), PUIS la remise fidélité s'applique.
 * En tranches, la 1ʳᵉ facture ne porte qu'UNE tranche (miroir de `RenewalBonusService`).
 */
function buildPriceSummary(input: {
    monthlyPrice: number
    hourlyPrice: number | null
    hoursPerMonth: number | null
    effectiveResumeDate: string | null
    loyaltyEligible: boolean
    loyaltyAmount: number
    installments: boolean
}): UIActivationPriceSummary {
    const { monthlyPrice, hourlyPrice, hoursPerMonth, effectiveResumeDate, loyaltyEligible, loyaltyAmount } = input

    const isProratedStart = isStartProrated(effectiveResumeDate)
    // `> 0`, pas `!= null` : `partialMonthHours` répond `undefined` sur 0 heure, et le produit
    // devenait `NaN €` dans le récapitulatif.
    const proratedHours =
        isProratedStart && hoursPerMonth != null && hoursPerMonth > 0
            ? partialMonthHours(effectiveResumeDate, hoursPerMonth)
            : undefined
    const base = proratedHours != null && hourlyPrice != null ? Math.round(proratedHours * hourlyPrice) : monthlyPrice
    const firstDeduction = input.installments ? Math.min(50, loyaltyAmount) : loyaltyAmount
    const firstMonthPrice = loyaltyEligible ? Math.max(0, base - firstDeduction) : base

    const discountLabel = loyaltyEligible
        ? input.installments
            ? `−50 €/mois sur ${Math.round(loyaltyAmount / 50)} mois`
            : "remise fidélité"
        : null
    const parts = [isProratedStart ? "prorata" : null, discountLabel].filter(Boolean)
    const resumeLabel = formatActivationDate(effectiveResumeDate)

    return {
        monthlyPrice: formatActivationPrice(monthlyPrice),
        hoursLine: hoursPerMonth != null ? `${hoursPerMonth} h par mois` : null,
        hourlyLine: hourlyPrice != null ? `soit ${Math.round(hourlyPrice)}€/h` : null,
        // La ligne ne se rend que lorsqu'elle DIFFÈRE du mensuel : sinon on lirait
        // « 1ᵉʳ mois 87 € puis 87 €/mois ».
        firstMonthLabel: firstMonthPrice !== monthlyPrice ? `1ᵉʳ mois (${parts.join(", ")})` : null,
        firstMonthPrice: firstMonthPrice !== monthlyPrice ? formatActivationPrice(firstMonthPrice) : null,
        resumeLine: resumeLabel ? `Reprise le ${resumeLabel}` : null,
    }
}

/** Une reprise qui ne tombe pas le 1ᵉʳ du mois est proratisée (`ActivationComposerScreen.js:488-491`). */
function isStartProrated(date: string | null): boolean {
    const start = date ? moment(date) : null

    return !!(start?.isValid() && start.date() > 1)
}

function buildSubmissionBody(input: {
    draft: ActivationDraft
    allocatable: boolean
    resumeDateLocked: boolean
    resumeOptions: ResumeDateOption[]
    /** Date libre : la première acceptée par le back, ou null pour la liste fermée. */
    freeResumeFrom: string | null
    inPlaceMilestoneId: string | number | null
    studentClass: string | null
}): Record<string, unknown> {
    const { draft, allocatable, resumeDateLocked, resumeOptions, freeResumeFrom, inPlaceMilestoneId, studentClass } =
        input
    const picked = draft.pickedResumeDate
    const pickedIsAccepted =
        picked != null &&
        (freeResumeFrom
            ? picked >= freeResumeFrom
            : resumeOptions.some((option) => !option.adHoc && option.value === picked))

    return {
        offer_type: draft.offerType,
        hours_per_week: draft.hoursPerWeek,
        with_commitment: draft.withCommitment,
        subject_ids: draft.subjectIds,
        hours_allocation: allocatable ? draft.hoursAllocation : null,
        // La date n'est envoyée que quand le back l'accepte : une date ad hoc reprise du jalon
        // serait refusée par le choix fermé du back, et son absence garde la date du jalon. Se
        // relire dans `resumeOptions` et pas dans la liste globale — elle seule sait ce que le
        // picker a réellement offert (la liste d'équipe ajoute le 1ᵉʳ octobre). Une date libre
        // (après un stage de l'année) est acceptée à partir de sa borne.
        ...(!resumeDateLocked && pickedIsAccepted ? { resume_date: picked } : {}),
        // Modification in place d'un jalon non parental — absent sinon.
        ...(inPlaceMilestoneId != null ? { milestone_id: inPlaceMilestoneId } : {}),
        // Jamais un tableau vide : champ absent = grille non touchée, on n'efface rien.
        ...(draft.slots.length > 0 ? { slots: draft.slots } : {}),
        // La classe n'est envoyée que si le parent l'a changée : écrire le profil reste un geste
        // explicite.
        ...(draft.pickedClass && draft.pickedClass !== studentClass ? { student_class: draft.pickedClass } : {}),
    }
}
